import { ManualBlogBriefEditor } from "../components/briefs/ManualBlogBriefEditor";
import { useCampaignActionPlans } from "../hooks/useCampaignActionPlans";
import { LoadErrorState } from "../components/ui/LoadState";
import { isNotFoundError } from "../lib/load-state";
import { CampaignOpportunitiesPanel } from "../components/campaign/CampaignOpportunitiesPanel";
import { CampaignDailyWorkPanel } from "../components/campaign/CampaignDailyWorkPanel";
import { CampaignBlogRevision } from "../components/review/CampaignBlogRevision";
import { DraftThisBrief } from "../components/campaign/DraftThisBrief";
import { BriefApprovalNote } from "../components/campaign/BriefApprovalNote";
import { ApprovedBlogPublishPanels, BlogPostPreview, isApprovedBlogPost } from "../components/review/BlogPublishPanels";
import { PreviewButton } from "../components/preview/PreviewButton";
import { RegenerateCanonicalBrief } from "../components/campaign/RegenerateCanonicalBrief";
import { BriefDraftAttemptPanel } from "../components/campaign/BlogGateFailure";
import { CampaignStatusControl } from "../components/campaign/CampaignStatusControl";
// Campaign detail — brief list, document preview, approve / request changes (PRD-0011)
// Activity tab — scoped run log for this campaign (PRD-0018)

import { useEffect, useState } from "react";
import { Markdown } from "../components/agent/Markdown";
import { Link, Navigate, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useIsAuthenticated, useAuth } from "../hooks/useAuth";
import { useBrand } from "../hooks/useBrand";
import { useInbox } from "../hooks/useInbox";
import { useSurfaces } from "../hooks/useSurfaces";
import {
  useCampaignDetail,
  useBriefDocument,
  useBriefEvents,
  useCreateBrief,
  useCreateCampaignPacket,
  useCampaignPackets,
  useSaveBriefDocument,
  useUpdateBrief,
} from "../hooks/useCampaigns";
import { useRunsByCampaign } from "../hooks/useRunsByCampaign";
import { WorkFeed } from "../components/work-feed/WorkFeed";
import { briefWorkItem, readWorkFeedFilter, workspaceWorkItems } from "../lib/work-feed";
import { withQueryParams } from "../lib/path-query";
import { BlockedBriefActions } from "../components/campaign/BlockedBriefActions";
import { WorkflowStepsPanel } from "../components/workflow/WorkflowStepsPanel";
import { timeAgo, formatDuration } from "../lib/utils";
import { taskRunJobLabel } from "../lib/task-run-presentation";
import {
  buildCampaignSurfaceOptions,
  datetimeLocalToIso,
  getCampaignSurfaceGuidance,
  isoToDatetimeLocal,
  pickDefaultCampaignSurface,
} from "../lib/campaign-surfaces";
import type { Brief, BriefDocType, Campaign, ContentPacket, Surface, Brand, TaskRun } from "../../../shared/types";
import { SCHEDULE_EDIT_BLOCKED_STATES as SCHEDULE_EDIT_BLOCKED_STATE_VALUES } from "../../../shared/campaign-constants";

const DOC_TABS: { key: BriefDocType; label: string }[] = [
  { key: "draft", label: "Draft" },
  { key: "voice_review", label: "Voice review" },
  { key: "brief", label: "Brief" },
  { key: "published", label: "Published" },
];

type CampaignTab = "briefs" | "activity" | "opportunities" | "daily";
type AddWorkMode = "brief" | "packet";
type ActivityFilter = "all" | "failed" | "completed";
const SCHEDULE_EDIT_BLOCKED_STATES = new Set<Brief["state"]>(SCHEDULE_EDIT_BLOCKED_STATE_VALUES);


function RunStatusDot({ status }: { status: TaskRun["status"] }) {
  if (status === "failed") return <span className="text-red-500">●</span>;
  if (status === "completed") return <span className="text-emerald-600">✓</span>;
  if (status === "skipped") return <span className="text-amber-500">–</span>;
  return <span className="text-blue-500">○</span>;
}

function ActivityTab({
  brandId,
  campaignId,
}: {
  brandId: string;
  campaignId: string;
}) {
  const { data, isLoading, isError, error } = useRunsByCampaign(brandId, campaignId);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const allRuns = data?.data ?? [];

  const filtered =
    filter === "all" ? allRuns : allRuns.filter((r) => r.status === filter);

  const filterButtons: { key: ActivityFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "failed", label: "Failed" },
    { key: "completed", label: "Completed" },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : "Failed to load activity";
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        <p>Could not load activity.</p>
        <details className="mt-2 text-xs text-red-900">
          <summary className="cursor-pointer font-medium">Details</summary>
          <p className="mt-1 font-mono">{msg}</p>
        </details>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {filterButtons.map((btn) => (
          <button
            key={btn.key}
            type="button"
            onClick={() => setFilter(btn.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              filter === btn.key
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {allRuns.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          No agent runs for this campaign yet
        </p>
      ) : filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          No {filter} runs
        </p>
      ) : (
        <>
          <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white shadow-sm">
            {filtered.map((run) => (
              <li key={run.id}>
                <Link
                  to={`/runs/${run.id}`}
                  className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50"
                >
                  <RunStatusDot status={run.status} />
                  <span className="flex-1 font-medium text-slate-900">
                    {taskRunJobLabel(run)}
                  </span>
                  <span className="text-slate-500">{timeAgo(run.started_at)}</span>
                  {run.completed_at && run.duration_ms != null && (
                    <span className="text-slate-400">
                      {formatDuration(run.duration_ms)}
                    </span>
                  )}
                  <span className="text-slate-400">→</span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-400">Showing {filtered.length} run{filtered.length !== 1 ? "s" : ""}</p>
        </>
      )}
    </div>
  );
}

function metadataScore(metadata: Record<string, unknown> | null | undefined): number | null {
  if (!metadata || typeof metadata !== "object") return null;
  const s = (metadata as { score?: unknown }).score;
  if (typeof s === "number" && !Number.isNaN(s)) return s;
  if (typeof s === "string") {
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function formatScheduledAt(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatCampaignOperatingDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function canEditScheduleMetadata(brief: Brief): boolean {
  return !brief.brief_locked_by && !brief.brief_locked_at && !SCHEDULE_EDIT_BLOCKED_STATES.has(brief.state);
}

function workflowContextForBrief(
  brandId: string,
  campaignId: string,
  brand: Brand | null | undefined,
  campaign: Campaign | null | undefined,
  brief: Brief,
) {
  return {
    brandId,
    campaignId,
    brand: brand ?? null,
    campaign: campaign ?? null,
    brief,
  };
}

export function CampaignDetail() {
  const { brandId, campaignId, briefId: routeBriefId } = useParams<{ brandId: string; campaignId: string; briefId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { loading } = useAuth();
  const { data: brandRes } = useBrand(brandId);
  const brand = brandRes?.data;
  const surfaces = useSurfaces(brandId).data?.data ?? [];
  const campaignSurfaceOptions = buildCampaignSurfaceOptions(surfaces);
  const defaultCampaignSurface = pickDefaultCampaignSurface(surfaces);

  const { data, isLoading, error } = useCampaignDetail(brandId, campaignId);
  const campaign = data?.data;
  const briefs = campaign?.briefs ?? [];

  const createBrief = useCreateBrief(brandId);
  const createPacket = useCreateCampaignPacket(campaignId, brandId);
  const updateBrief = useUpdateBrief(campaignId, brandId);
  const packetsQ = useCampaignPackets(campaignId, brandId);
  const campaignInbox = useInbox(brandId);

  const [documentSearch, setDocumentSearch] = useSearchParams();
  const campaignTab: CampaignTab = documentSearch.get("view") === "activity" ? "activity" : documentSearch.get("view") === "opportunities" ? "opportunities" : documentSearch.get("view") === "daily" ? "daily" : "briefs";
  const setCampaignTab = (tab: CampaignTab) => { const next = new URLSearchParams(documentSearch); next.set("view", tab === "activity" || tab === "opportunities" || tab === "daily" ? tab : "work"); setDocumentSearch(next); };
  const feedFilter = readWorkFeedFilter(documentSearch.get("filter"), "all");
  const campaignPath = `/brand/${brandId}/campaigns/${campaignId}`;
  const withFeedFilter = (path: string) => withQueryParams(path, { filter: feedFilter });
  const campaignReturn = `${campaignPath}${location.search}`;
  const campaignNotice = (location.state as { campaignNotice?: string } | null)?.campaignNotice;
  const newWorkPage = location.pathname.endsWith("/work/new");
  const newPacketPage = location.pathname.endsWith("/packets/new");
  const schedulePage = location.pathname.endsWith("/schedule");
  const previewBriefId = routeBriefId ?? null;
  const setPreviewBriefId = (id: string | null) => navigate(id ? `/brand/${brandId}/campaigns/${campaignId}/briefs/${id}` : `/brand/${brandId}/campaigns/${campaignId}`);
  const [docTab, setDocTab] = useState<BriefDocType>(documentSearch.get("document") === "brief" ? "brief" : "draft");
  useEffect(() => { setDocTab(documentSearch.get("document") === "brief" ? "brief" : "draft"); }, [documentSearch, routeBriefId]);
  const addBriefOpen = newWorkPage || newPacketPage;
  const setAddBriefOpen = (open: boolean) => { if (!open) navigate(campaignReturn); };
  const [addWorkMode, setAddWorkMode] = useState<AddWorkMode>(newPacketPage ? "packet" : "brief");
  const [nbTitle, setNbTitle] = useState("");
  const [nbPlatform, setNbPlatform] = useState("");
  const [nbFormat, setNbFormat] = useState("");
  const [nbSurface, setNbSurface] = useState<Surface>(defaultCampaignSurface);
  const [packetDraftBody, setPacketDraftBody] = useState("");
  const [packetAngleBrief, setPacketAngleBrief] = useState("");
  const [packetReviewReport, setPacketReviewReport] = useState("");
  const [packetApprovalPacket, setPacketApprovalPacket] = useState("");
  const [packetSourceUrls, setPacketSourceUrls] = useState("");
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [addBriefError, setAddBriefError] = useState<string | null>(null);
  const editBriefId = schedulePage ? routeBriefId ?? null : null;
  const setEditBriefId = (id: string | null) => navigate(id ? withFeedFilter(`${campaignPath}/briefs/${id}/schedule`) : withQueryParams(`${campaignPath}/briefs/${routeBriefId}`, { document: "brief", filter: feedFilter }));
  const [editSurface, setEditSurface] = useState<Surface>(defaultCampaignSurface);
  const [editScheduledAt, setEditScheduledAt] = useState("");
  const [editBriefError, setEditBriefError] = useState<string | null>(null);
  const addBriefSurfaceGuidance = getCampaignSurfaceGuidance(surfaces, nbSurface);
  const editSurfaceGuidance = getCampaignSurfaceGuidance(surfaces, editSurface);
  const packets = packetsQ.data?.data ?? [];
  const packetByBriefId = new Map(packets.map((packet) => [packet.brief_id, packet] as const));
  useEffect(() => { setAddWorkMode(newPacketPage ? "packet" : "brief"); }, [newPacketPage]);
  useEffect(() => {
    if (!schedulePage || !routeBriefId) return;
    const brief = briefs.find(item => item.id === routeBriefId);
    if (!brief) return;
    setEditSurface((brief.surface ?? defaultCampaignSurface) as Surface);
    setEditScheduledAt(isoToDatetimeLocal(brief.scheduled_at));
    setEditBriefError(null);
  }, [schedulePage, routeBriefId, campaign?.id]);
  const actionPlansQ = useCampaignActionPlans(brandId, campaignId);
  const workItems = workspaceWorkItems({ brandId: brandId ?? "", inbox: campaignInbox.items.filter(item => item.campaignId === campaignId), briefs: briefs.filter(brief => brief.brand_id === brandId && brief.campaign_id === campaignId), runs: [] }).map(item => ({ ...item, href: item.href.startsWith(campaignPath) ? withFeedFilter(item.href) : item.href }));
  const plannedItems = (actionPlansQ.data?.data ?? []).filter(plan => plan.brand_id === brandId && plan.campaign_id === campaignId).map(plan => ({ id: `plan:${plan.id}`, title: plan.title, status: plan.status === "archived" ? "Archived plan" : "Planned", context: "Action plan", reason: plan.objective, attention: false, complete: false, updatedAt: plan.updated_at, href: withFeedFilter(`${campaignPath}/action-plans/${encodeURIComponent(plan.id)}`), actionLabel: "Open plan" }));


  const previewDoc = useBriefDocument(
    campaignId,
    previewBriefId ?? undefined,
    docTab,
    !!previewBriefId && !schedulePage,
  );
  // Only a 404 means "no document yet"; any other failure leaves the content unknown.
  const previewDocFailed = previewDoc.isError && !isNotFoundError(previewDoc.error);

  const previewEvents = useBriefEvents(campaignId, previewBriefId ?? undefined, !!previewBriefId && !schedulePage);
  const saveBriefDoc = useSaveBriefDocument(brandId);
  const [docEditText, setDocEditText] = useState("");
  const [docEditing, setDocEditing] = useState(false);
  useEffect(() => {
    setDocEditing(false);
    setDocEditText("");
  }, [previewBriefId, docTab]);

  useEffect(() => {
    if (docEditing) return;
    const c = previewDoc.data?.data?.content;
    if (typeof c === "string") setDocEditText(c);
  }, [previewDoc.data?.data?.content, docTab, previewBriefId, docEditing]);

  // "Revise brief" from a blocked draft attempt: switch to the brief and open it for editing once it loads.
  const [reviseRequested, setReviseRequested] = useState(false);
  useEffect(() => {
    if (!reviseRequested || docTab !== "brief" || previewDoc.isLoading || previewDoc.isFetching) return;
    setReviseRequested(false);
    setDocEditText(previewDoc.data?.data?.content ?? "");
    setDocEditing(true);
  }, [reviseRequested, docTab, previewDoc.isLoading, previewDoc.isFetching, previewDoc.data?.data?.content]);

  useEffect(() => {
    if (!campaignSurfaceOptions.some((option) => option.value === nbSurface)) {
      setNbSurface(defaultCampaignSurface);
    }
    if (!campaignSurfaceOptions.some((option) => option.value === editSurface)) {
      setEditSurface(defaultCampaignSurface);
    }
  }, [campaignSurfaceOptions, defaultCampaignSurface, editSurface, nbSurface]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!brandId || !campaignId) return <Navigate to="/home" replace />;
  if (campaign && campaign.brand_id !== brandId) return <section className="space-y-3"><h1 className="text-2xl font-semibold">Campaign unavailable</h1><p role="alert">This campaign is not available in the selected brand.</p><Link to={`/brand/${brandId}/campaigns`}>Back to campaigns</Link></section>;

  function resetAddWorkForm() {
    setAddWorkMode("brief");
    setNbTitle("");
    setNbPlatform("");
    setNbFormat("");
    setNbSurface(defaultCampaignSurface);
    setPacketDraftBody("");
    setPacketAngleBrief("");
    setPacketReviewReport("");
    setPacketApprovalPacket("");
    setPacketSourceUrls("");
    setAddBriefError(null);
  }

  function packetSourceRefsFromText(value: string): Array<{ label: string | null; url: string }> {
    return value
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .flatMap((line) => {
        const parts = line.split("|").map((part) => part.trim());
        if (parts.length === 1) return [{ label: null, url: parts[0]! }];
        const [label, url] = parts;
        return url ? [{ label: label || null, url }] : [];
      });
  }

  async function handleAddBrief(e: React.FormEvent) {
    e.preventDefault();
    if (!campaignId || !nbTitle.trim()) return;
    setAddBriefError(null);
    if (addWorkMode === "packet" && !packetDraftBody.trim()) {
      setAddBriefError("Draft body is required for a content packet.");
      return;
    }
    try {
      if (addWorkMode === "packet") {
        await createPacket.mutateAsync({
          title: nbTitle.trim(),
          platform: nbPlatform.trim() || null,
          format: nbFormat.trim() || null,
          surface: nbSurface,
          status: "pending_approval",
          draft_body: packetDraftBody.trim(),
          angle_brief: packetAngleBrief.trim() || null,
          review_report: packetReviewReport.trim() || null,
          approval_packet: packetApprovalPacket.trim() || null,
          source_refs: packetSourceRefsFromText(packetSourceUrls),
        });
      } else {
        const basePayload = {
          campaignId,
          title: nbTitle.trim(),
        };
        const created = await createBrief.mutateAsync({ ...basePayload, platform: nbPlatform.trim() || null, format: nbFormat.trim() || null, surface: nbSurface });
        if (nbSurface === "blog") {
          resetAddWorkForm();
          navigate(`${campaignPath}/briefs/${created.data.id}?document=brief`);
          return;
        }
      }
      setAddBriefOpen(false);
      resetAddWorkForm();
    } catch (err) {
      setAddBriefError(err instanceof Error ? err.message : (addWorkMode === "packet" ? "Could not create content packet" : "Could not create brief"));
    }
  }

  async function handleUpdateSchedule(e: React.FormEvent) {
    e.preventDefault();
    if (!editBriefId) return;
    const scheduledAt = datetimeLocalToIso(editScheduledAt);
    if (editScheduledAt && !scheduledAt) {
      setEditBriefError("Schedule must be a valid date and time.");
      return;
    }
    setEditBriefError(null);
    try {
      await updateBrief.mutateAsync({
        briefId: editBriefId,
        surface: editSurface,
        scheduled_at: scheduledAt,
      });
      setEditBriefId(null);
    } catch (err) {
      setEditBriefError(err instanceof Error ? err.message : "Could not update brief schedule");
    }
  }

  const previewBrief = previewBriefId ? briefs.find((b) => b.id === previewBriefId && b.brand_id === brandId && b.campaign_id === campaignId) : null;
  const previewPacket = previewBrief ? packetByBriefId.get(previewBrief.id) ?? null : null;
  const voiceScoreFromPreview =
    previewDoc.data?.data?.metadata != null
      ? metadataScore(previewDoc.data.data.metadata as Record<string, unknown>)
      : null;

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex min-h-[30vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error instanceof Error ? error.message : "Failed to load campaign"}
        </div>
      )}
      {!isLoading && campaign && campaign.brand_id === brandId && !routeBriefId && !addBriefOpen && (
        <section className="mx-auto w-full max-w-4xl min-w-0 space-y-6">
          <header className="space-y-4">
            <Link to={`/brand/${brandId}/campaigns`} className="inline-flex min-h-11 items-center text-sm text-slate-600 hover:underline">← Campaigns</Link>
            <div className="flex flex-wrap items-start justify-between gap-4"><div className="min-w-0 flex-1 basis-64"><p className="text-xs font-medium capitalize text-slate-500">{campaign.status}</p><h1 className="mt-2 break-words text-3xl font-semibold tracking-tight text-slate-900">{campaign.title}</h1><p className="mt-2 break-words text-sm leading-6 text-slate-600">{campaign.goal || "Add an objective in Edit campaign."}</p>{campaign.deadline && <p className="mt-2 text-xs text-slate-500">Planning deadline: {new Date(campaign.deadline).toLocaleDateString()}</p>}{(campaign.starts_at || campaign.ends_at) && <p className="mt-2 text-xs text-slate-500">Operating window: {campaign.starts_at ? `From ${formatCampaignOperatingDate(campaign.starts_at)}` : "No start date"}; {campaign.ends_at ? `through ${formatCampaignOperatingDate(campaign.ends_at)}` : "ongoing"}<span className="ml-1">(shown in your local time)</span></p>}</div>
              <div className="flex flex-wrap items-center gap-3">{brandId && <CampaignStatusControl brandId={brandId} campaign={campaign} />}<Link to={`${campaignPath}/edit?return=${encodeURIComponent(campaignReturn)}`} className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">Edit campaign</Link><Link to={`${campaignPath}/action-plans/new${location.search}`} className="inline-flex min-h-11 items-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700">Add action</Link></div>
            </div>
          </header>
          {campaignNotice && <p role="status" className="text-sm text-brand-700">{campaignNotice}</p>}
          <div className="flex flex-wrap items-center gap-4 border-b border-slate-200">
            {(["briefs", "activity", "opportunities", "daily"] as CampaignTab[]).map(tab => <button key={tab} type="button" aria-pressed={campaignTab === tab} onClick={() => setCampaignTab(tab)} className={`min-h-11 border-b-2 px-1 text-sm ${campaignTab === tab ? "border-brand-600 font-medium text-brand-700" : "border-transparent text-slate-600"}`}>{tab === "briefs" ? "Work" : tab === "activity" ? "Activity" : tab === "opportunities" ? "Opportunities" : "Daily"}</button>)}
          </div>
          {campaignTab === "briefs" ? <WorkFeed items={[...workItems, ...plannedItems]} filter={feedFilter} loading={campaignInbox.isLoading || actionPlansQ.isLoading} incomplete={campaignInbox.status === "error" || actionPlansQ.isError} onFilterChange={value => { const next = new URLSearchParams(documentSearch); next.set("filter", value); setDocumentSearch(next); }} emptyAction={{ href: `${campaignPath}/action-plans/new${location.search}`, label: "Add action" }} /> : campaignTab === "activity" ? <ActivityTab brandId={brandId} campaignId={campaignId} /> : campaignTab === "opportunities" ? <CampaignOpportunitiesPanel brandId={brandId ?? ""} campaignId={campaignId ?? ""} /> : <CampaignDailyWorkPanel brandId={brandId ?? ""} campaignId={campaignId ?? ""} />}
          {actionPlansQ.isError && <p role="alert" className="text-sm text-amber-900">Action plans could not load. <button className="min-h-11 underline" onClick={() => void actionPlansQ.refetch()}>Retry plans</button></p>}
          {campaignInbox.status === "error" && <p role="alert" className="text-sm text-amber-900">Review and gate status could not be fully loaded. <button className="min-h-11 underline" onClick={() => void campaignInbox.refetch()}>Retry decisions</button></p>}
          <details className="border-t border-slate-200 pt-4 text-sm"><summary className="min-h-11 cursor-pointer text-slate-600">More</summary><Link to={`${campaignPath}/work/new${location.search}`} className="mr-5 inline-flex min-h-11 items-center text-brand-700 hover:underline">Plan content brief</Link><Link to={`${campaignPath}/packets/new${location.search}`} className="inline-flex min-h-11 items-center text-brand-700 hover:underline">Import content packet</Link></details>
        </section>
      )}

      {routeBriefId && !isLoading && campaign && !previewBrief && <p role="alert">This document is not in this campaign. <Link to={`/brand/${brandId}/campaigns/${campaignId}`}>Back to campaign</Link></p>}
      {previewBriefId && previewBrief && !schedulePage && (
        <section aria-label="Campaign document" className="mx-auto w-full max-w-4xl min-w-0">
          <Link to={withFeedFilter(campaignPath)} className="mb-4 inline-flex min-h-11 items-center text-sm text-slate-600 hover:underline">← Back to campaign</Link>
          <div className="min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{previewBrief.title}</h1>
                  {voiceScoreFromPreview != null && (
                    <p className="mt-1 text-sm text-slate-600">
                      Voice score:{" "}
                      <span className="font-semibold text-slate-900">
                        {voiceScoreFromPreview.toFixed(1)}
                      </span>
                      /10
                    </p>
                  )}
                </div>

              </div>
              <label className="mt-4 flex items-center gap-3 text-sm text-slate-600">
                Document
                <select value={docTab} onChange={event => setDocTab(event.target.value as BriefDocType)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-slate-900">
                  {DOC_TABS.map(tab => <option key={tab.key} value={tab.key}>{tab.label}</option>)}
                </select>
              </label>
              {brandId && <div className="mt-3"><PreviewButton brandId={brandId} brief={previewBrief} /></div>}
            </div>
            {brandId && <BlockedBriefActions key={previewBrief.id} brief={previewBrief} brandId={brandId} />}
            <div className="min-w-0 px-5 py-6 sm:px-8">
              {previewDoc.isLoading && (
                  <div className="flex justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                  </div>
              )}
              {!previewDoc.isLoading && (docTab === "draft" || docTab === "brief") && campaignId && previewBriefId ? (
                docEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={docEditText}
                      onChange={(e) => setDocEditText(e.target.value)}
                      rows={16}
                      className="w-full border border-slate-300 px-3 py-2 font-mono text-sm text-slate-800"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void saveBriefDoc.mutateAsync({
                          campaignId,
                          briefId: previewBriefId,
                          type: docTab === "draft" ? "draft" : "brief",
                          content: docEditText,
                        }).then(() => setDocEditing(false))}
                        disabled={saveBriefDoc.isPending}
                        className="rounded-xl bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                      >
                        {saveBriefDoc.isPending ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDocEditing(false);
                          setDocEditText(previewDoc.data?.data?.content ?? "");
                        }}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : previewDocFailed ? (
                  <LoadErrorState
                    compact
                    title="This document could not be loaded"
                    description="Editing is paused so an existing draft is not overwritten."
                    onRetry={() => void previewDoc.refetch()}
                  />
                ) : previewDoc.isError || !(previewDoc.data?.data?.content ?? "").trim() ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">
                      {previewDoc.isError ? "No document for this type yet — add one below." : "No content yet."}
                    </p>
                    <textarea
                      value={docEditText}
                      onChange={(e) => setDocEditText(e.target.value)}
                      rows={12}
                      placeholder="Write or paste content…"
                      className="w-full border border-slate-300 px-3 py-2 font-mono text-sm text-slate-800"
                    />
                    <button
                      type="button"
                      onClick={() => void saveBriefDoc.mutateAsync({
                        campaignId,
                        briefId: previewBriefId,
                        type: docTab === "draft" ? "draft" : "brief",
                        content: docEditText,
                      })}
                      disabled={saveBriefDoc.isPending || !docEditText.trim()}
                      className="rounded-xl bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {saveBriefDoc.isPending ? "Saving…" : docTab === "draft" ? "Save draft" : "Save brief"}
                    </button>
                  </div>
                ) : (
                  <div>
                    {docTab === "draft" && brandId && isApprovedBlogPost(previewBrief)
                      ? <BlogPostPreview brief={previewBrief} brandId={brandId} content={previewDoc.data!.data!.content} />
                      : <Markdown content={previewDoc.data!.data!.content} reading titleAlreadyShown={previewBrief?.title} />}
                    <button
                      type="button"
                      onClick={() => {
                        setDocEditText(previewDoc.data!.data!.content ?? "");
                        setDocEditing(true);
                      }}
                      className="mt-3 min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                  </div>
                )
              ) : (
                !previewDoc.isLoading && (
                  <>
                    {previewDocFailed ? (
                      <LoadErrorState compact title="This document could not be loaded" onRetry={() => void previewDoc.refetch()} />
                    ) : previewDoc.isError && (
                      <p className="text-sm text-slate-500">No document for this type yet.</p>
                    )}
                    {previewDoc.data?.data?.content != null && (
                      <Markdown content={previewDoc.data.data.content} reading titleAlreadyShown={previewBrief?.title} />
                    )}
                  </>
                )
              )}
            </div>
              {previewPacket && (
                <details className="border-t border-slate-200 px-5 py-3 text-sm text-slate-700">
                  <summary className="min-h-11 cursor-pointer font-medium">Sources and review context</summary>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-blue-800 ring-1 ring-inset ring-blue-200">Content packet</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200">{previewPacket.status.replace(/_/g, " ")}</span>
                  </div>
                  {previewPacket.angle_brief && <p className="mt-2"><span className="font-semibold">Angle brief:</span> {previewPacket.angle_brief}</p>}
                  {previewPacket.review_report && <p className="mt-2"><span className="font-semibold">Review report:</span> {previewPacket.review_report}</p>}
                  {previewPacket.approval_packet && <p className="mt-2"><span className="font-semibold">Approval packet:</span> {previewPacket.approval_packet}</p>}
                  {previewPacket.source_refs.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-700">
                      {previewPacket.source_refs.map((ref) => (
                        <li key={ref.url}>{ref.label ? `${ref.label}: ` : ""}{ref.url}</li>
                      ))}
                    </ul>
                  )}
                </details>
              )}
            {docTab === "draft" && !docEditing && brandId && campaignId && previewBriefId
              && (briefs.find((brief) => brief.id === previewBriefId)?.surface ?? briefs.find((brief) => brief.id === previewBriefId)?.platform) === "blog" && (
              <CampaignBlogRevision campaignId={campaignId} briefId={previewBriefId} brandId={brandId} />
            )}
            {brandId && <ApprovedBlogPublishPanels key={previewBrief.id} brief={previewBrief} brandId={brandId} />}
            {docTab === "brief" && !docEditing && !previewDoc.isLoading && brandId && campaignId && previewBrief.surface === "blog" && ["ideation", "briefed", "blocked"].includes(previewBrief.state) && <ManualBlogBriefEditor key={`${previewBrief.id}:${previewDoc.data?.data?.document_key ?? "new"}`} brandId={brandId} campaignId={campaignId} briefId={previewBrief.id} metadata={previewDoc.data?.data?.metadata} content={previewDoc.data?.data?.content} />}
            {docTab === "brief" && !docEditing && brandId && campaignId && <RegenerateCanonicalBrief brandId={brandId} campaignId={campaignId} brief={{ id: previewBrief.id, state: previewBrief.state }} />}
            {brandId && campaignId && <BriefDraftAttemptPanel brandId={brandId} campaignId={campaignId} brief={{ id: previewBrief.id, state: previewBrief.state }} onReviseBrief={() => { setDocTab("brief"); setReviseRequested(true); }} />}
            {campaignId && <BriefApprovalNote campaignId={campaignId} brief={{ id: previewBrief.id, state: previewBrief.state, surface: previewBrief.surface ?? previewBrief.platform ?? null }} />}
            {brandId && campaignId && <DraftThisBrief brandId={brandId} campaignId={campaignId} brief={{ id: previewBrief.id, state: previewBrief.state, surface: previewBrief.surface ?? previewBrief.platform ?? null }} campaign={campaign ? { status: campaign.status, updated_at: campaign.updated_at } : undefined} />}
            <details className="border-t border-slate-200 px-5 py-3 text-sm">
              <summary className="min-h-11 cursor-pointer font-medium text-slate-700">Action details and controls</summary>
              <p className="mt-2 text-slate-600">{briefWorkItem(previewBrief).reason}</p>
              <div className="mt-3 flex flex-wrap gap-4">
                {["in_review", "approved", "scheduled", "blocked"].includes(previewBrief.state) && <Link to={`/review?brandId=${encodeURIComponent(brandId)}&briefId=${encodeURIComponent(previewBrief.id)}`} className="inline-flex min-h-11 items-center text-brand-700 hover:underline">Open Review</Link>}
                {canEditScheduleMetadata(previewBrief) && <Link to={withFeedFilter(`${campaignPath}/briefs/${previewBrief.id}/schedule`)} className="inline-flex min-h-11 items-center text-brand-700 hover:underline">Schedule action</Link>}
                {!["published", "cancelled"].includes(previewBrief.state) && <Link to={`/agent/${brandId}?briefId=${encodeURIComponent(previewBrief.id)}`} className="inline-flex min-h-11 items-center text-brand-700 hover:underline">Work with agent</Link>}
              </div>
              {workflowError && <p role="alert" className="mt-3 text-red-800">{workflowError}</p>}
              <WorkflowStepsPanel workflowId="campaign-brief-text" context={workflowContextForBrief(brandId, campaignId, brand, campaign, previewBrief)} onError={setWorkflowError} />
            </details>
            {previewEvents.data?.data && previewEvents.data.data.length > 0 && (
              <details className="border-t border-slate-200 px-5 py-3">
                <summary className="min-h-11 cursor-pointer text-sm font-medium text-slate-700">Decision history</summary>
                <ul className="space-y-2 text-sm text-slate-600">
                  {previewEvents.data.data.slice(-8).map((ev) => (
                    <li key={ev.id}>
                      {(ev.from_state ?? "start")} → {ev.to_state}
                      {ev.reason ? ` — ${ev.reason}` : ""}{" "}
                      <span className="text-slate-400">
                        {new Date(ev.created_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </section>
      )}

      {/* Addressable plan and import pages */}
      {addBriefOpen && campaign && campaign.brand_id === brandId && campaignId && (
        <section className="mx-auto w-full max-w-2xl space-y-5">
          <Link to={campaignReturn} className="inline-flex min-h-11 items-center text-sm text-slate-600 hover:underline">← {campaign.title}</Link>
          <form className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 sm:p-6" onSubmit={(e) => void handleAddBrief(e)}>
            <h1 className="text-2xl font-semibold text-slate-900">{addWorkMode === "packet" ? "Import content packet" : "Add action"}</h1>
            <p className="text-sm text-slate-600">{addWorkMode === "packet" ? "Save a draft and its source context to this campaign." : "Plan a content brief. Save first; choose Run or Schedule separately."}</p>
            {addBriefError && (
              <p className="mt-2 text-sm text-red-700">{addBriefError}</p>
            )}
            <label className="mt-4 block text-sm font-medium text-slate-700">
              Title
              <input
                type="text"
                value={nbTitle}
                onChange={(e) => setNbTitle(e.target.value)}
                required
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                placeholder="e.g. Launch week LinkedIn thread"
              />
            </label>
            <label className="mt-3 block text-sm font-medium text-slate-700">
              Platform (optional)
              <input
                type="text"
                value={nbPlatform}
                onChange={(e) => setNbPlatform(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 block text-sm font-medium text-slate-700">
              Format (optional)
              <input
                type="text"
                value={nbFormat}
                onChange={(e) => setNbFormat(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-medium text-slate-700">
                Surface
                <select
                  value={nbSurface}
                  onChange={(e) => setNbSurface(e.target.value as Surface)}
                  className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                >
                  {campaignSurfaceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {addWorkMode === "packet" && <p className="text-sm text-slate-600">Imported content enters human Review. Importing does not approve or publish it.</p>}
            </div>
            {addWorkMode === "packet" && (
              <>
                <label className="mt-3 block text-sm font-medium text-slate-700">
                  Draft body
                  <textarea
                    value={packetDraftBody}
                    onChange={(e) => setPacketDraftBody(e.target.value)}
                    required
                    rows={6}
                    className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Paste the reviewed draft body"
                  />
                </label>
                <label className="mt-3 block text-sm font-medium text-slate-700">
                  Angle brief
                  <textarea
                    value={packetAngleBrief}
                    onChange={(e) => setPacketAngleBrief(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Why this angle exists"
                  />
                </label>
                <label className="mt-3 block text-sm font-medium text-slate-700">
                  Review report
                  <textarea
                    value={packetReviewReport}
                    onChange={(e) => setPacketReviewReport(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Review findings or approval rationale"
                  />
                </label>
                <label className="mt-3 block text-sm font-medium text-slate-700">
                  Approval packet
                  <textarea
                    value={packetApprovalPacket}
                    onChange={(e) => setPacketApprovalPacket(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Optional owner-facing approval packet"
                  />
                </label>
                <label className="mt-3 block text-sm font-medium text-slate-700">
                  Source refs
                  <textarea
                    value={packetSourceUrls}
                    onChange={(e) => setPacketSourceUrls(e.target.value)}
                    rows={3}
                    className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
                    placeholder="One per line. Use label | https://example.com"
                  />
                </label>
              </>
            )}
            <div
              className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
                addBriefSurfaceGuidance.status === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : addBriefSurfaceGuidance.status === "matched"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-slate-50 text-slate-700"
              }`}
            >
              <p className="font-medium">{addBriefSurfaceGuidance.title}</p>
              <p className="mt-1">{addBriefSurfaceGuidance.detail}</p>
              <Link
                to={`/brand/${brandId}/settings/surfaces`}
                className="mt-2 inline-flex font-medium underline underline-offset-2"
              >
                Manage brand surfaces
              </Link>
            </div>
            <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {addWorkMode === "brief"
                ? "Saving adds an unscheduled content brief. No drafting, Run or publishing starts from this form."
                : "Content packets preserve draft body, review context, approval packet, and source refs on the draft document so the operator can review the full packet in-app."}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAddBriefOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createBrief.isPending || createPacket.isPending}
                className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {createBrief.isPending || createPacket.isPending
                  ? "Creating…"
                  : addWorkMode === "packet"
                    ? "Save packet for review"
                    : "Save action"}
              </button>
            </div>
          </form>
        </section>
      )}

      {editBriefId && previewBrief && campaign?.brand_id === brandId && !canEditScheduleMetadata(previewBrief) && <p role="alert">This action cannot be rescheduled in its current state. <Link to={withFeedFilter(`${campaignPath}/briefs/${routeBriefId}`)}>Back to action</Link></p>}
      {editBriefId && previewBrief && campaign?.brand_id === brandId && canEditScheduleMetadata(previewBrief) && (
        <section className="mx-auto w-full max-w-2xl space-y-5">
          <Link to={withQueryParams(`${campaignPath}/briefs/${routeBriefId}`, { document: "brief", filter: feedFilter })} className="inline-flex min-h-11 items-center text-sm text-slate-600 hover:underline">← Back to action</Link>
          <form className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 sm:p-6" onSubmit={(e) => void handleUpdateSchedule(e)}>
            <h2 className="text-lg font-semibold text-slate-900">Edit schedule</h2>
            <p className="mt-1 text-sm text-slate-500">
              This updates schedule metadata only. It does not reset or retry the brief state.
            </p>
            {editBriefError && (
              <p className="mt-2 text-sm text-red-700">{editBriefError}</p>
            )}
            <label className="mt-4 block text-sm font-medium text-slate-700">
              Surface
              <select
                value={editSurface}
                onChange={(e) => setEditSurface(e.target.value as Surface)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
              >
                {campaignSurfaceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div
              className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
                editSurfaceGuidance.status === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : editSurfaceGuidance.status === "matched"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-slate-50 text-slate-700"
              }`}
            >
              <p className="font-medium">{editSurfaceGuidance.title}</p>
              <p className="mt-1">{editSurfaceGuidance.detail}</p>
              <Link
                to={`/brand/${brandId}/settings/surfaces`}
                className="mt-2 inline-flex font-medium underline underline-offset-2"
              >
                Manage brand surfaces
              </Link>
            </div>
            <label className="mt-3 block text-sm font-medium text-slate-700">
              Scheduled for
              <input
                type="datetime-local"
                value={editScheduledAt}
                onChange={(e) => setEditScheduledAt(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditBriefId(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateBrief.isPending}
                className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {updateBrief.isPending ? "Saving…" : "Save schedule"}
              </button>
            </div>
          </form>
        </section>
      )}


    </div>
  );
}
